import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DEFAULT_PAGE_SIZE } from '@/__core/constants/pagination';
import { Alert, Box, Snackbar } from '@mui/material';
import { useSelector } from 'react-redux';

import AppListToolbarGlobal from '@/modules/inventory/items/components/ItemsToolbarGlobal';
import { resolveActiveCompanyFilter, resolveToolbarTabCompanyFilter } from '@/__core/hooks/useCompanyContext';
import { useModuleTabsWithGuard } from '@/__core/hooks/useModuleTabsWithGuard';
import { getCurrentCompanyId } from '@/modules/auth/helpers/CompanyStorage';
import { RootState } from '@/setup/redux/RootReducer';
import MainListModule from '@/__core/layout/module/MainListModule';
import { useModuleToolbarPermissions, isTabVisibleForToolbarCompany } from '@/__core/permissions';
import { TabData } from '@/__core/types/TabData';
import ModuleTabsHeader from '@/modules/users/components/ModuleTabsHeader';
import { getRequestMaterialStatuses, RequestMaterialStatusOption } from '@/modules/inventory/requestMaterial/redux/RequestMaterialStatusCRUD';

import { createDefaultItemForm, getItemGroupOptions, getItemManufacturerOptions, getItemSupplierOptions, getItemUomOptions, InventoryItemFormData, InventoryItemRow, itemStatusOptions, loadItemGroupOptions, loadItemManufacturerOptions, loadItemSupplierOptions, loadItemTypeOptions, loadItemUomOptions } from '../models/ItemModel';
import { createItem, deleteItem, getItem, getItems, updateItem, updateItemStatus } from '../redux';
import ItemForm from './ItemForm';
import ItemList, { ItemListHandle } from './ItemList';
import { ItemDeleteModal } from './modals';

const buildFormMetadata = (
  formData: InventoryItemFormData,
  itemId?: string,
  originalFormData?: InventoryItemFormData,
) => ({ formData, itemId, originalFormData });

const buildUpdatePayload = (
  original: InventoryItemFormData | undefined,
  current: InventoryItemFormData,
): Partial<InventoryItemFormData> => {
  if (!original) return current;

  const diff: Partial<InventoryItemFormData> = {};
  const fields: (keyof InventoryItemFormData)[] = [
    'code', 'name', 'group_id', 'supplier_id', 'item_type',
    'uom_id', 'manufacturer_id', 'status', 'company_id',
    'specification', 'image1', 'image2', 'track_lot', 'track_location',
  ];

  fields.forEach((field) => {
    if (current[field] !== original[field]) {
      (diff as any)[field] = current[field];
    }
  });

  return diff;
};
const PAGE_SIZE = DEFAULT_PAGE_SIZE;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const DEFAULT_ITEM_STATUS_CODE = 'YEU_CAU_VAT_TU';

const isPersistedItemId = (value?: string) => Boolean(value && UUID_PATTERN.test(value));

const getDefaultItemStatus = (options: RequestMaterialStatusOption[]) => {
  return options.find((option) => option.code === DEFAULT_ITEM_STATUS_CODE)
    || options.find((option) => option.id === DEFAULT_ITEM_STATUS_CODE)
    || options[0]
    || null;
};

const toSelectableStatusOptions = (options: RequestMaterialStatusOption[]) => {
  const activeOptions = options.filter((option) => option.id && option.id !== 'all' && option.is_active !== false);
  if (!activeOptions.length) {
    return itemStatusOptions;
  }

  return [{ id: 'all', name: 'Tất cả' }, ...activeOptions];
};

const resolveCompanyIdForQuery = (companyId?: string) => {
  if (!companyId || companyId === 'all' || companyId === 'global' || !UUID_PATTERN.test(companyId)) {
    return '';
  }
  return companyId;
};

export default function ItemCatalogModule() {
  const selectedCompany = useSelector((state: RootState) => state.auth.selectedCompany);

  const [rows, setRows] = useState<InventoryItemRow[]>([]);
  const [snackbarMessage, setSnackbarMessage] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [filters, setFilters] = useState({ status: 'all', company: 'all' });
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [pagination, setPagination] = useState<{ count: number; page: number; pageSize: number; totalPages: number }>({ count: 0, page: 1, pageSize: PAGE_SIZE, totalPages: 1 });
  const [listLoading, setListLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupOptionsVersion, setGroupOptionsVersion] = useState(0);
  const [formStatusOptions, setFormStatusOptions] = useState<Array<{ id: string; name: string; code?: string }>>(itemStatusOptions);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const listRef = useRef<ItemListHandle>(null);
  const [dirtyTabIds, setDirtyTabIds] = useState<Record<number, boolean>>({});

  const selectedCompanyId = useMemo(
    () => String(selectedCompany?.company_id || getCurrentCompanyId() || '').trim(),
    [selectedCompany?.company_id],
  );

  const activeCompanyFilter = useMemo(
    () => resolveActiveCompanyFilter(selectedCompanyId, filters.company),
    [selectedCompanyId, filters.company],
  );

  const toolbarTabCompanyFilter = useMemo(
    () => resolveToolbarTabCompanyFilter(filters.company),
    [filters.company],
  );

  const {
    canCreate: canCreateItems,
    canUpdate: canUpdateItems,
    canDelete: canDeleteItems,
    resolveTabPermissions,
    resolvePermissionsForCompanyId,
  } = useModuleToolbarPermissions({
    resource: 'inventory.item',
    activeCompanyFilter,
    updateTabTypes: ['update-item'],
  });

  const listScopeCompanyId = useMemo(
    () => resolveCompanyIdForQuery(activeCompanyFilter),
    [activeCompanyFilter],
  );

  const defaultTabs = useMemo(() => {
    const tabs: TabData[] = [
      { id: 0, title: 'Danh mục vật tư', type: 'list', closable: false, createdAt: Date.now() },
    ];
    if (canCreateItems) {
      tabs.push({ id: 10, title: '', type: 'add', closable: false, createdAt: Date.now() });
    }
    return tabs;
  }, [canCreateItems]);

  const {
    storedTabs,
    activeTab,
    removeTab,
    updateTab,
    tabs,
    visibleActiveTab,
    handleVisibleTabChange,
    isStoredTabPanelActive,
    handleCloseTab,
    handleAddTab,
    handleUnsavedChangesUpdate,
    guardAndRun,
    openTab,
    unsavedDialog,
  } = useModuleTabsWithGuard({
    storageKey: 'inventory_item_catalog_tabs',
    defaultTabs,
    maxTabs: 12,
    canCreate: Boolean(canCreateItems),
    toolbarCompanyId: toolbarTabCompanyFilter,
    guardedTabTypes: ['new-item', 'update-item'],
    allowMultipleTabTypes: ['new-item', 'update-item'],
    buildNewTab: (currentTabs) => {
      const baseCompanyId = listScopeCompanyId || selectedCompanyId;
      return {
        id: Date.now() + Math.floor(Math.random() * 1000),
        title: `Vật tư #${currentTabs.filter((tab) => tab.type === 'new-item').length + 1}`,
        type: 'new-item',
        closable: true,
        metadata: {
          companyId: baseCompanyId,
          ...buildFormMetadata(createDefaultItemForm(baseCompanyId)),
        },
      };
    },
  });

  const activeTabData = storedTabs[activeTab];
  const activeFormData = activeTabData?.metadata?.formData as InventoryItemFormData | undefined;
  const activeItemIdRaw = (
    activeTabData?.metadata?.itemId
    || activeTabData?.entityId
  ) as string | undefined;
  const activeItemId = isPersistedItemId(activeItemIdRaw) ? activeItemIdRaw : undefined;
  const activeCompanyId = activeFormData?.company_id || listScopeCompanyId || selectedCompanyId;

  const handleToolbarFilterChange = (field: string, value: string) => {
    setPagination((current) => ({ ...current, page: 1 }));
    setFilters((current) => ({ ...current, [field]: value }));
  };

  useEffect(() => {
    const timeout = window.setTimeout(async () => {
      setListLoading(true);
      try {
        const response = await getItems({
          search: searchQuery,
          status: filters.status,
          companyId: listScopeCompanyId,
          page: pagination.page,
          pageSize: pagination.pageSize,
        });

        setRows(response.results || []);
        setPagination({
          count: response.count || 0,
          page: response.page || 1,
          pageSize: response.page_size || PAGE_SIZE,
          totalPages: response.total_pages || 1,
        });
      } catch (error: any) {
        setSnackbarMessage({
          message: error?.message || 'Không thể tải danh mục vật tư.',
          severity: 'error',
        });
      } finally {
        setListLoading(false);
      }
    }, 200);

    return () => window.clearTimeout(timeout);
  }, [listScopeCompanyId, searchQuery, filters.status, pagination.page, pagination.pageSize]);

  const companyOptionsLoadedRef = useRef(false);

  useEffect(() => {
    const companyId = activeCompanyId;
    if (!companyId) {
      setFormStatusOptions(itemStatusOptions);
      return;
    }

    let mounted = true;
    void Promise.all([
      loadItemGroupOptions(),
      loadItemManufacturerOptions(companyId),
      loadItemSupplierOptions(companyId),
      loadItemTypeOptions(),
      loadItemUomOptions(companyId),
      getRequestMaterialStatuses(companyId).catch(() => [] as RequestMaterialStatusOption[]),
    ])
      .then(([, , , , , backendStatuses]) => {
        if (mounted) {
          setFormStatusOptions(toSelectableStatusOptions(backendStatuses));
          companyOptionsLoadedRef.current = true;
          setGroupOptionsVersion((value) => value + 1);
        }
      })
      .catch((error: any) => {
        if (mounted) {
          console.error('ItemCatalogModule: failed to load company options', error);
        }
      });

    return () => {
      mounted = false;
    };
  }, [activeCompanyId]);

  useEffect(() => {
    if (!activeTabData?.id || !activeFormData || !companyOptionsLoadedRef.current) return;

    const companyId = activeFormData.company_id;
    let disposed = false;

    getRequestMaterialStatuses(companyId)
      .then((backendStatuses) => {
        if (disposed) return;

        const nextStatusOptions = toSelectableStatusOptions(backendStatuses);
        const defaultStatus = getDefaultItemStatus(backendStatuses);
        const matchedStatus = nextStatusOptions.find((option) => option.id === activeFormData.status || option.code === activeFormData.status);
        const resolvedStatusId = matchedStatus?.id || defaultStatus?.id || '';

        if (resolvedStatusId && activeFormData.status !== resolvedStatusId) {
          updateTab(activeTabData.id, {
            metadata: buildFormMetadata({
              ...activeFormData,
              status: resolvedStatusId,
            }, activeItemIdRaw),
          });
        }
      })
      .catch(() => {});

    return () => {
      disposed = true;
    };
  }, [activeTabData?.id]);

  const updateFormData = (updates: Partial<InventoryItemFormData>) => {
    if (!activeTabData?.id || !activeFormData) return;
    const nextFormData = { ...activeFormData, ...updates };
    updateTab(activeTabData.id, { metadata: buildFormMetadata(nextFormData, activeItemId) });
    handleUnsavedChangesUpdate(true, activeTabData.id);
    setDirtyTabIds((current) => ({ ...current, [activeTabData.id]: true }));
  };

  const openExistingItem = (row: InventoryItemRow) => {
    guardAndRun(() => {
      void (async () => {
        try {
          const detail = await getItem(row.id);
          const initialFormData: InventoryItemFormData = {
            code: detail.code,
            name: detail.name,
            group_id: detail.group_id,
            supplier_id: detail.supplier_id,
            item_type: detail.item_type,
            uom_id: detail.uom_id,
            manufacturer_id: detail.manufacturer_id,
            status: detail.status,
            company_id: detail.company_id,
            specification: detail.specification,
            image1: detail.image1,
            image2: detail.image2,
            track_lot: detail.track_lot,
            track_location: detail.track_location,
          };
          openTab({
            id: Date.now(),
            title: row.code,
            type: 'update-item',
            closable: true,
            entityId: row.id,
            metadata: {
              companyId: detail.company_id,
              ...buildFormMetadata(initialFormData, detail.id, { ...initialFormData }),
            },
          });
        } catch (error: any) {
          setSnackbarMessage({ message: error?.message || 'Không thể tải chi tiết vật tư.', severity: 'error' });
        }
      })();
    });
  };

  const reloadCurrentPage = async (page = pagination.page) => {
    const response = await getItems({
      search: searchQuery,
      status: filters.status,
      companyId: listScopeCompanyId,
      page,
      pageSize: pagination.pageSize,
    });

    setRows(response.results || []);
    setPagination({
      count: response.count || 0,
      page: response.page || 1,
      pageSize: response.page_size || PAGE_SIZE,
      totalPages: response.total_pages || 1,
    });
  };



  const handleSave = async (): Promise<string | false> => {
    if (!activeFormData || !activeTabData?.id) return false;

    const isUpdateMode = Boolean(activeItemId);
    const savePermissions = resolvePermissionsForCompanyId(activeFormData.company_id);
    if (isUpdateMode && !savePermissions.canUpdate) {
      setSnackbarMessage({ message: 'Ban khong co quyen cap nhat vat tu.', severity: 'error' });
      return false;
    }
    if (!isUpdateMode && !savePermissions.canCreate) {
      setSnackbarMessage({ message: 'Ban khong co quyen tao vat tu.', severity: 'error' });
      return false;
    }

    if (!UUID_PATTERN.test(activeFormData.company_id)) {
      setSnackbarMessage({ message: 'Công ty chưa hợp lệ. Vui lòng chọn lại công ty.', severity: 'error' });
      return false;
    }

    if (!UUID_PATTERN.test(activeFormData.supplier_id)) {
      setSnackbarMessage({ message: 'Nhà cung cấp chưa được đồng bộ. Vui lòng chọn lại từ danh sách.', severity: 'error' });
      return false;
    }

    if (!UUID_PATTERN.test(activeFormData.uom_id)) {
      setSnackbarMessage({ message: 'ĐVT chưa hợp lệ. Vui lòng chọn lại ĐVT.', severity: 'error' });
      return false;
    }

    if (activeFormData.group_id && !UUID_PATTERN.test(activeFormData.group_id)) {
      setSnackbarMessage({ message: 'Nhóm vật tư chưa hợp lệ. Vui lòng chọn lại nhóm vật tư.', severity: 'error' });
      return false;
    }

    if (activeFormData.manufacturer_id && !UUID_PATTERN.test(activeFormData.manufacturer_id)) {
      setSnackbarMessage({ message: 'Nhà sản xuất chưa hợp lệ. Vui lòng chọn lại nhà sản xuất.', severity: 'error' });
      return false;
    }

    setSaveLoading(true);
    try {
      const trimmedFormData: InventoryItemFormData = {
        ...activeFormData,
        code: activeFormData.code.trim(),
        name: activeFormData.name.trim(),
        specification: activeFormData.specification.trim(),
      };

      let savedItem: InventoryItemRow;
      if (activeItemId) {
        const diffPayload = buildUpdatePayload(
          activeTabData.metadata?.originalFormData as InventoryItemFormData | undefined,
          trimmedFormData,
        );
        savedItem = await updateItem(activeItemId, diffPayload);
      } else {
        savedItem = await createItem(trimmedFormData);
      }

      const nextId = savedItem.id || activeItemId;

      handleUnsavedChangesUpdate(false, activeTabData.id);
      setDirtyTabIds((current) => ({ ...current, [activeTabData.id]: false }));
      setSnackbarMessage({ message: `Đã lưu ${savedItem.code}.`, severity: 'success' });
      const updatedFormData: InventoryItemFormData = {
        code: savedItem.code,
        name: savedItem.name,
        group_id: savedItem.group_id,
        supplier_id: savedItem.supplier_id,
        item_type: savedItem.item_type,
        uom_id: savedItem.uom_id,
        manufacturer_id: savedItem.manufacturer_id,
        status: savedItem.status,
        company_id: savedItem.company_id,
        specification: savedItem.specification,
        image1: savedItem.image1,
        image2: savedItem.image2,
        track_lot: savedItem.track_lot,
        track_location: savedItem.track_location,
      };
      updateTab(activeTabData.id, {
        title: savedItem.code || savedItem.name,
        entityId: nextId,
        metadata: buildFormMetadata(updatedFormData, nextId, { ...updatedFormData }),
      });
      await reloadCurrentPage(activeItemId ? pagination.page : 1);
      return nextId || false;
    } catch (error: any) {
      setSnackbarMessage({ message: error?.message || 'Không thể lưu vật tư.', severity: 'error' });
      return false;
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!activeItemId || !activeTabData?.id) {
      return;
    }

    if (!canDeleteItems) {
      setSnackbarMessage({ message: 'Ban khong co quyen xoa vat tu.', severity: 'error' });
      return;
    }

    setDeleteLoading(true);
    try {
      await deleteItem(activeItemId);
      setDeleteDialogOpen(false);
      handleUnsavedChangesUpdate(false, activeTabData.id);
      setDirtyTabIds((current) => ({ ...current, [activeTabData.id]: false }));
      removeTab(activeTabData.id);
      setSnackbarMessage({ message: 'Đã xóa vật tư.', severity: 'success' });
      await reloadCurrentPage(pagination.page);
    } catch (error: any) {
      setSnackbarMessage({ message: error?.message || 'Không thể xóa vật tư.', severity: 'error' });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleBulkDelete = async (itemIds: string[]) => {
    if (!itemIds.length) {
      return;
    }

    if (!canDeleteItems) {
      setSnackbarMessage({ message: 'Ban khong co quyen xoa vat tu.', severity: 'error' });
      return;
    }

    try {
      await Promise.all(itemIds.map((id) => deleteItem(id)));
      setSelectedItemIds([]);
      setSnackbarMessage({ message: `Đã xóa ${itemIds.length} vật tư.`, severity: 'success' });
      await reloadCurrentPage(pagination.page);
    } catch (error: any) {
      setSnackbarMessage({ message: error?.message || 'Không thể xóa danh sách vật tư đã chọn.', severity: 'error' });
    }
  };

  const handleBulkStatusChange = async (itemIds: string[], status: 'draft' | 'approved' | 'inactive') => {
    if (!itemIds.length) {
      return;
    }

    if (!canUpdateItems) {
      setSnackbarMessage({ message: 'Ban khong co quyen cap nhat vat tu.', severity: 'error' });
      return;
    }

    try {
      await Promise.all(itemIds.map((id) => updateItemStatus(id, status)));
      setSnackbarMessage({ message: `Đã cập nhật trạng thái cho ${itemIds.length} vật tư.`, severity: 'success' });
      await reloadCurrentPage(pagination.page);
    } catch (error: any) {
      setSnackbarMessage({ message: error?.message || 'Không thể cập nhật trạng thái cho danh sách vật tư đã chọn.', severity: 'error' });
    }
  };

  const handleSearchInputChange = (value: string) => {
    setSearchInputValue(value);
    if (value === '') {
      setSearchQuery('');
    }
  };

  const handleSearchSubmit = () => {
    setSearchQuery(searchInputValue.trim());
    setPagination((current) => ({ ...current, page: 1 }));
  };

  const buildFormUpdates = (currentFormData: InventoryItemFormData, updates: Partial<InventoryItemFormData>) => {
    if (!updates.company_id || updates.company_id === currentFormData.company_id) {
      return { ...currentFormData, ...updates };
    }

    const companyDefaults = createDefaultItemForm(updates.company_id);
    return {
      ...currentFormData,
      ...updates,
      group_id: companyDefaults.group_id,
      supplier_id: companyDefaults.supplier_id,
      uom_id: companyDefaults.uom_id,
      manufacturer_id: companyDefaults.manufacturer_id,
      status: updates.status || companyDefaults.status,
    };
  };

  return (
    <>
      <ModuleTabsHeader
        tabs={tabs}
        activeTab={visibleActiveTab}
        currentTabType={activeTabData?.type}
        onTabChange={handleVisibleTabChange}
        onCloseTab={handleCloseTab}
        onAddTab={handleAddTab}
        moduleType='inventory'
        unsavedDialog={unsavedDialog}
      />

      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Box sx={{ flex: 1, display: 'flex' }}>
          <Box sx={{ position: 'relative', width: '100%' }}>
            {storedTabs.map((tab, index) => {
              if (!isTabVisibleForToolbarCompany(tab, toolbarTabCompanyFilter)) {
                return null;
              }
              const isActive = isStoredTabPanelActive(tab);
              const tabFormData = tab.metadata?.formData as InventoryItemFormData | undefined;
              const tabPermissions = resolveTabPermissions(tab);
              return (
                <Box key={tab.id} sx={{ display: isActive ? 'flex' : 'none', width: '100%', height: '100%' }}>
                  {tab.type === 'list' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
                      <MainListModule
                        leftSidebar={null}
                        toolbar={
                          <AppListToolbarGlobal
                            searchQuery={searchInputValue}
                            onSearchChange={handleSearchInputChange}
                            onSearchSubmit={handleSearchSubmit}
                            onToggleAdvancedSearch={() =>
                              setIsAdvancedSearchOpen((current) => !current)
                            }
                            isAdvancedSearchOpen={isAdvancedSearchOpen}
                            filters={{
                              company: filters.company || 'all',
                              status: filters.status,
                            }}
                            onFilterChange={handleToolbarFilterChange}
                            statusOptions={formStatusOptions}
                            selectedCount={selectedItemIds.length}
                            onDelete={() => listRef.current?.requestDelete()}
                            canDelete={canDeleteItems}
                          />
                        }
                        dataTable={
                          <ItemList
                            ref={listRef}
                            rows={rows}
                            loading={listLoading}
                            pagination={pagination}
                            onPageChange={(_event, page) => setPagination((current) => ({ ...current, page }))}
                            onSelectRow={openExistingItem}
                            onSelectionChange={setSelectedItemIds}
                            onBulkDelete={handleBulkDelete}
                            onBulkStatusChange={handleBulkStatusChange}
                            canUpdate={canUpdateItems}
                            canDelete={canDeleteItems}
                          />
                        }
                      />
                    </Box>
                  ) : tabFormData ? (
                    <Box sx={{ width: '100%', height: '100%', overflow: 'auto', overflowX: 'hidden', p: 2, boxSizing: 'border-box' }}>
                      <ItemForm
                        key={`item-form-${tab.id}-${groupOptionsVersion}`}
                        itemId={isPersistedItemId(tab.metadata?.itemId as string | undefined)
                          ? tab.metadata?.itemId as string
                          : undefined}
                        transactionCompanyId={listScopeCompanyId || selectedCompanyId}
                        formData={tabFormData}
                        supplierOptions={getItemSupplierOptions(tabFormData.company_id)}
                        manufacturerOptions={getItemManufacturerOptions(tabFormData.company_id)}
                        groupOptions={getItemGroupOptions(tabFormData.company_id)}
                        uomOptions={getItemUomOptions(tabFormData.company_id)}
                        onChangeForm={(updates) => {
                          const nextFormData = buildFormUpdates(tabFormData, updates);
                          if (tab.id !== activeTabData?.id) {
                            updateTab(tab.id, { metadata: buildFormMetadata(nextFormData, tab.metadata?.itemId as string | undefined) });
                            handleUnsavedChangesUpdate(true, tab.id);
                            setDirtyTabIds((current) => ({ ...current, [tab.id]: true }));
                            return;
                          }
                          updateFormData(nextFormData);
                        }}
                        onSave={handleSave}
                        onCancel={() => handleCloseTab(tab.id)}
                        onDelete={() => {
                          if (!canDeleteItems) {
                            setSnackbarMessage({ message: 'Ban khong co quyen xoa vat tu.', severity: 'error' });
                            return;
                          }
                          setDeleteDialogOpen(true);
                        }}
                        canEdit={tab.type === 'new-item' ? tabPermissions.canCreate : tabPermissions.canUpdate}
                        isDirty={!!dirtyTabIds[tab.id]}
                        isPersisted={isPersistedItemId(tab.metadata?.itemId as string | undefined)}
                        canDelete={Boolean(canDeleteItems && activeItemId && tab.id === activeTabData?.id)}
                        saving={saveLoading}
                        deleting={deleteLoading}
                      />
                    </Box>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      <Snackbar open={Boolean(snackbarMessage)} autoHideDuration={3500} onClose={() => setSnackbarMessage(null)}>
        <Alert severity={snackbarMessage?.severity || 'success'} onClose={() => setSnackbarMessage(null)} sx={{ width: '100%' }}>
          {snackbarMessage?.message}
        </Alert>
      </Snackbar>

      <ItemDeleteModal
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={() => { void handleDelete(); }}
        isLoading={deleteLoading}
      />
    </>
  );
}
